import { useCallback, useId, useMemo, type FC } from "react";
import { atom, useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { atomEffect } from "jotai-effect";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { InputField } from "@/components/podkit/forms/InputField";
import type { PlainWorkflow } from "@/queries/automation-queries";
import { WorkflowEditAction } from "@/components/automations/AutomationEditAction";
import { WorkflowEditTriggers } from "@/components/automations/AutomationEditTriggers";
import { WorkflowEditExecutor } from "@/components/automations/AutomationEditExecutor";
import {
    convertTriggersToFormData,
    convertWorkflowActionToFormSteps,
    convertWorkflowActionToFormLimits,
    WorkflowEditFormTriggerContextType,
    type WorkflowEditFormData,
    type WorkflowEditFormStepData,
    type WorkflowEditFormTriggerData,
} from "@/components/automations/automation-edit-form-data";

export const WorkflowEditForm: FC<{
    workflow: PlainWorkflow;
    formName: string;
    onDisabledChange: (disabled: boolean) => void;
    onSubmit: (values: WorkflowEditFormData) => void;
}> = ({ workflow, formName, onSubmit, onDisabledChange }) => {
    const executorId = useId();

    // Compute initial values once
    const initialValues = useMemo(() => {
        const limits = convertWorkflowActionToFormLimits(workflow.spec?.action);
        return {
            executor: workflow.metadata?.executor,
            steps: convertWorkflowActionToFormSteps(workflow.spec?.action),
            triggers: convertTriggersToFormData(workflow.spec?.triggers ?? []),
            maxParallel: limits?.maxParallel,
            maxTotal: limits?.maxTotal,
        };
    }, [workflow]);

    // Inline primitive atoms with initial values
    const executorAtom = useMemo(
        () => atom<PlainMessage<Subject> | undefined>(initialValues.executor),
        [initialValues.executor],
    );
    const stepsAtom = useMemo(() => atom<WorkflowEditFormStepData[]>(initialValues.steps), [initialValues.steps]);
    const triggersAtom = useMemo(
        () => atom<WorkflowEditFormTriggerData[]>(initialValues.triggers),
        [initialValues.triggers],
    );
    const maxParallelAtom = useMemo(
        () => atom<number | undefined>(initialValues.maxParallel),
        [initialValues.maxParallel],
    );
    const maxTotalAtom = useMemo(() => atom<number | undefined>(initialValues.maxTotal), [initialValues.maxTotal]);

    // Derived atom for form validation
    const isFormValidAtom = useMemo(
        () =>
            atom((get) => {
                const triggers = get(triggersAtom);

                const hasAtLeastOneTrigger = triggers.length > 0;

                const allManualTriggersValid = triggers.every((trigger) => {
                    if (trigger.type === "manual") {
                        switch (trigger.context?.type) {
                            case WorkflowEditFormTriggerContextType.Projects:
                                return trigger.context.projectIds.length > 0;
                            case WorkflowEditFormTriggerContextType.Repositories:
                                return trigger.context.repoSearchString.length > 0;
                            default:
                                return true;
                        }
                    }
                    return true;
                });

                return hasAtLeastOneTrigger && allManualTriggersValid;
            }),
        [triggersAtom],
    );

    // Derived atom for change detection
    const isFormChangedAtom = useMemo(
        () =>
            atom((get) => {
                const executorChanged = JSON.stringify(get(executorAtom)) !== JSON.stringify(initialValues.executor);
                const stepsChanged = JSON.stringify(get(stepsAtom)) !== JSON.stringify(initialValues.steps);
                const triggersChanged = JSON.stringify(get(triggersAtom)) !== JSON.stringify(initialValues.triggers);
                const maxParallelChanged = get(maxParallelAtom) !== initialValues.maxParallel;
                const maxTotalChanged = get(maxTotalAtom) !== initialValues.maxTotal;

                return (
                    executorChanged ||
                    stepsChanged ||
                    triggersChanged ||
                    maxParallelChanged ||
                    maxTotalChanged
                );
            }),
        [
            executorAtom,
            stepsAtom,
            triggersAtom,
            maxParallelAtom,
            maxTotalAtom,
            initialValues,
        ],
    );

    // Derived atom for disabled state
    const isFormDisabledAtom = useMemo(
        () =>
            atom((get) => {
                const isValid = get(isFormValidAtom);
                const isChanged = get(isFormChangedAtom);
                return !isValid || !isChanged;
            }),
        [isFormValidAtom, isFormChangedAtom],
    );

    // AtomEffect to trigger onDisabledChange callback
    const disabledChangeEffect = useMemo(
        () =>
            atomEffect((get) => {
                const isDisabled = get(isFormDisabledAtom);
                onDisabledChange(isDisabled);
            }),
        [isFormDisabledAtom, onDisabledChange],
    );
    useAtomValue(disabledChangeEffect);

    // Read executor for the executor selector
    const executor = useAtomValue(executorAtom);

    const handleExecutorChange = useAtomCallback(
        useCallback(
            (_get, set, newExecutor: PlainMessage<Subject> | undefined) => {
                set(executorAtom, newExecutor);
            },
            [executorAtom],
        ),
    );

    const handleSubmit = useAtomCallback(
        useCallback(
            (get, _set, event: React.FormEvent) => {
                event.preventDefault();
                const isDisabled = get(isFormDisabledAtom);
                if (isDisabled) return;

                const maxParallel = get(maxParallelAtom);
                const maxTotal = get(maxTotalAtom);

                onSubmit({
                    executor: get(executorAtom),
                    steps: get(stepsAtom),
                    triggers: get(triggersAtom),
                    limits: maxParallel !== undefined || maxTotal !== undefined ? { maxParallel, maxTotal } : undefined,
                });
            },
            [
                onSubmit,
                isFormDisabledAtom,
                executorAtom,
                stepsAtom,
                triggersAtom,
                maxParallelAtom,
                maxTotalAtom,
            ],
        ),
    );

    return (
        <form id={formName} name={formName} onSubmit={handleSubmit} className="flex flex-col gap-6">
            <InputField
                label="Run as"
                hint="Select who runs this automation, you or a service account"
                id={executorId}
            >
                <WorkflowEditExecutor
                    selectedExecutorId={executor?.id}
                    onExecutorChange={handleExecutorChange}
                    className="w-[250px] min-w-fit max-w-[700px]"
                />
            </InputField>
            <WorkflowEditTriggers workflow={workflow} triggersAtom={triggersAtom} />
            <WorkflowEditAction maxParallelAtom={maxParallelAtom} maxTotalAtom={maxTotalAtom} />
        </form>
    );
};
